import { useState, useEffect } from 'react';
import { apiFetch } from './config';
import { useLensWeights, lensWeight, LensWeightSliders, AxisWeightSliders, DEFAULT_ENGINE } from './lensWeights';
import { Explain } from './Explain';
import './css/trust.css';

// The global viewpoint drawer (UX rebuild wave 5): lens weights + engine
// choice in ONE place, reachable from the nav on every page. The viewpoint is
// explicitly the viewer's — there is no single trust algorithm to hide behind.

export default function ViewpointDrawer({ open, onClose }) {
  const { engine, setEngine } = useLensWeights();
  const [lenses, setLenses] = useState([]);
  const [engines, setEngines] = useState([]);
  const [axes, setAxes] = useState([]);

  useEffect(() => {
    apiFetch('/api/trust/lenses')
      .then((r) => (r.ok ? r.json() : []))
      .then(setLenses)
      .catch(() => {});
    apiFetch('/api/trust/engines')
      .then((r) => (r.ok ? r.json() : []))
      .then(setEngines)
      .catch(() => {});
    apiFetch('/api/trust/axes')
      .then((r) => (r.ok ? r.json() : []))
      .then(setAxes)
      .catch(() => {});
  }, []);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--wv-shadow)', zIndex: 90 }}
      />
      {/* layout lives in app.css (.vp-drawer): side panel on desktop, bottom
          sheet ≤640px (2026-07-25 mobile-feel pass) */}
      <aside className="trust-page vp-drawer">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <h2 style={{ margin: 0, fontSize: '17px' }}>Your viewpoint</h2>
          <button onClick={onClose} style={{ fontSize: '13px' }}>close</button>
        </div>
        <p className="trust-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
          There is no single trust algorithm here. These controls are your blend —
          every score, color and ranking follows it, on every page.
        </p>

        <h3 style={{ fontSize: '14px', marginBottom: '6px' }}>
          <Explain concept="lens-weights">Lens weights</Explain>
        </h3>
        {lenses.length > 0 ? (
          <LensWeightSliders lenses={lenses} />
        ) : (
          <p className="trust-muted" style={{ fontSize: '12px' }}>Loading lenses…</p>
        )}

        {axes.length > 0 && (
          <>
            <h3 style={{ fontSize: '14px', margin: '18px 0 4px' }}>
              <Explain
                title="Standalone axes"
                text="Every relation type is its own axis (vector trust, 2026-07-26): activated edges tally per relation name, edge polarity as the sign. Axes start at weight ZERO — visible everywhere, in your blend nowhere — until you raise one here. supports/contradicts are not listed: they ARE the band."
              >
                Axes
              </Explain>
            </h3>
            <AxisWeightSliders axes={axes} />
          </>
        )}

        <h3 style={{ fontSize: '14px', margin: '18px 0 4px' }}>
          <Explain concept="engine">Trust engine</Explain>
        </h3>
        <p className="trust-muted" style={{ fontSize: '11.5px', marginTop: 0 }}>
          Engines score people, not claims. Your selection drives the inspector’s
          ranking; the lenses read EigenTrust as their reputation input.
        </p>
        {engines.map((e) => (
          <label
            key={e.id}
            title={e.description}
            style={{
              display: 'block',
              padding: '7px 9px',
              border: '1px solid ' + (engine === e.id ? 'var(--wv-accent)' : 'var(--wv-border)'),
              borderRadius: '8px',
              marginBottom: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <input
              type="radio"
              name="viewpoint-engine"
              checked={engine === e.id}
              onChange={() => setEngine(e.id)}
              style={{ marginRight: '8px' }}
            />
            {e.label}
            {e.id === DEFAULT_ENGINE && (
              <span className="trust-muted" style={{ fontSize: '11px' }}> · default</span>
            )}
            {e.status === 'diagnostic' && (
              <span className="stub-badge" style={{ marginLeft: '6px' }}>diagnostic — a flag, not a score</span>
            )}
            <span className="trust-muted" style={{ display: 'block', fontSize: '11px', marginTop: '3px' }}>
              {e.description}
            </span>
          </label>
        ))}
      </aside>
    </>
  );
}

// Small nav affordance: shows a dot when the viewpoint differs from default.
export function viewpointTouched(weights, engine, lensIds = []) {
  if (engine !== DEFAULT_ENGINE) return true;
  return Object.keys(weights).some((id) => lensWeight(weights, id) !== 1);
}
